<template>
  <ViewWrapper :scroll-steps="scrollSteps" class="home">
    <ScrollFade :scroll-position="scrollPosition" :min="0.3">
      <div class="home_bg"></div>
    </ScrollFade>

    <template #header>
      <ScrollFade :scroll-position="scrollPosition" :min="0.3">
        <h2 class="home__header">{{ $tc('view.home.enterName.value') }}</h2>
      </ScrollFade>
    </template>

    <ScrollFade
      #default="{ id }"
      :scroll-position="scrollPosition"
      :min-max-map="{
        introduction: [{ max: showCtaValue }],
        cta: [{ min: showCtaValue }],
      }"
    >
      <h2 v-if="id === 'introduction'" class="home__headline">
        <i18n path="view.home.cta.value">
          <template #valuesIntoValue>
            <br />
            <b class="typo_hl">{{ $t('view.home.cta.valuesIntoValue') }}</b>
            <br />
          </template>
        </i18n>
      </h2>
      <div v-else-if="id === 'cta'" class="input-company">
        <!-- <h2 class="headline">{{ $tc('view.home.enterName.value') }}</h2> -->

        <form @submit="handleFormSubmit">
          <div class="line">
            <div
              :class="[
                aimateDanger ? 'animate-danger progress-line' : 'progress-line',
                (!validInput && inputLength > 2) || !validInput ? 'color-red' : 'color-white',
              ]"
              :style="{
                transform: `scaleX(${Math.min(inputLength * 0.125, 1)}) translate3d(0,-25%,0)`,
              }"
            ></div>
          </div>

          <input
            id="nameInput"
            ref="nameInput"
            v-model="name"
            type="text"
            required
            minlength="3"
            maxlength="8"
            pattern="[a-zA-Z0-9-]+"
            @keydown="onKeyDown"
            @keyup="onKeyUp"
            @blur="onBlur"
          />

          <div class="line"></div>

          <span
            :class="[(!validInput && name.length > 2) || !validInput ? 'color-red' : 'color-grey']"
            >Maximum 8, non-special characters</span
          >
        </form>

        <div class="home__continue_button mobile-only">
          <ButtonPill replace :disabled="isDisabled" @click="handleFormSubmit">
            <input type="submit" :value="$tc('view.home.enterName.submit')" />
          </ButtonPill>
        </div>
      </div>
    </ScrollFade>

    <template #footer>
      <transition name="fade" mode="out-in">
        <InteractionHint
          v-if="scrollPosition <= showCtaValue"
          class="home__scrollhint"
          :label="$t('ui.interactionHint.scroll')"
          icon="arrows-down"
          animation-class="moveUpDown"
          :interaction-timeout="2000"
          :interaction-trigger="scrollPosition"
        />
        <div v-else class="home__continue_button desktop-only">
          <ButtonPill replace :disabled="isDisabled" @click="handleFormSubmit">
            <input type="submit" :value="$tc('view.home.enterName.submit')" />
          </ButtonPill>
        </div>
      </transition>
    </template>
  </ViewWrapper>
</template>

<script>
import { gsap, ScrollToPlugin } from 'gsap/all';
import store from '../store';
import { isIOS, isAndroid } from '../utils/helpers';

import ButtonPill from '../components/ButtonPill';
import { playSound } from '../services/sound';

import InteractionHint from '../components/InteractionHint';
import ScrollFade from '../components/ScrollFade';
import ViewWrapper from '../components/ViewWrapper';

gsap.registerPlugin(ScrollToPlugin);

const SCROLL_STEPS = 1.5;
const SHOW_CTA_VALUE = 0.3;
const SOUND_RESET_TIME = 500;

export default {
  name: 'Home',

  components: {
    InteractionHint,
    ScrollFade,
    ViewWrapper,
    ButtonPill,
  },

  props: {
    scrollPosition: {
      type: Number,
      default: undefined,
    },
  },

  data() {
    return {
      scrollSteps: SCROLL_STEPS,
      showCtaValue: SHOW_CTA_VALUE,
      channelAPlaying: false,
      channelBPlaying: false,
      name: '',
      inputValue: '',
      inputLength: '',
      validInput: true,
      isDisabled: true,
      aimateDanger: false,
      inputIsFocussed: false,
    };
  },

  computed: {
    userCount: () => store.userCount,
    inputPattern: () => {
      return new RegExp(/^[A-Za-z0-9]+$/);
    },
    isIOS,
    isAndroid,
  },

  watch: {
    scrollPosition: {
      immediate: true,
      handler(scrollPosition) {
        store.setGlobeTransitionValue(scrollPosition);

        this.playZoomSound(scrollPosition);

        // Auto focus the name input field
        if (scrollPosition > this.showCtaValue) {
          if (!this.inputIsFocussed) {
            const _this = this;
            this.inputIsFocussed = true;

            // Wait until the last element is faded out so the nameInput exists in the dom
            gsap.delayedCall(0.6, () => {
              if (this.isIOS || this.isAndroid) {
                _this.$refs.nameInput.placeholder = 'Name';
              } else {
                const input = _this.$refs.nameInput;
                if (input) input.focus();
              }
            });
          }
        } else {
          this.inputIsFocussed = false;
        }
      },
    },
  },

  mounted() {
    playSound('fxA', 'headline');
  },

  beforeRouteEnter(from, to, next) {
    // set new user model or reset user model for new game
    store.initUserModel();
    window.scrollTo(0, 0);
    next();
  },

  methods: {
    scrollDown() {
      gsap.to(window, { duration: 2.5, scrollTo: { y: 'max' } });
    },

    playZoomSound(scrollPosition) {
      if (scrollPosition > 0.7) return;

      // about every second scroll tick
      const mod = parseInt(scrollPosition * 100);
      // we reset the sound here early, because we want to recreate
      // that zoom noise from e.g. magnifying glasses in movies :P
      if (mod % 2) {
        if (this.channelAPlaying) return;
        this.channelAPlaying = true;
        playSound('fxA', 'user');
        setTimeout(() => {
          this.channelAPlaying = false;
        }, SOUND_RESET_TIME);
      } else {
        if (this.channelBPlaying) return;
        this.channelBPlaying = true;
        playSound('fxB', 'user');
        setTimeout(() => {
          this.channelBPlaying = false;
        }, SOUND_RESET_TIME);
      }
    },

    onKeyDown(event) {
      // const max = 8; // Number of maximum input characters
      this.inputValue = document.querySelector('#nameInput').value;
      this.inputLength = this.inputValue.length;

      if (this.inputLength === 8 && event.key != 'Backspace') {
        this.setAnimateDanger(true);

        const _this = this;
        gsap.delayedCall(0.2, () => {
          _this.setAnimateDanger(false);
        });
      }
    },

    onKeyUp() {
      const max = 8;
      const input = document.querySelector('#nameInput');
      this.inputValue = input.value;
      this.inputLength = this.inputValue.length;

      // Check if input only consists of letters and numbers
      // If the name is 0 letters we auto set validInput to true to avoid flickering
      this.validInput =
        this.inputLength > max
          ? false
          : this.inputLength > 0
          ? /^[A-Za-z0-9]+$/.test(this.inputValue)
          : true;

      this.isDisabled = !this.validInput || this.inputLength < 3 ? true : false;

      if (this.inputLength > max) {
        this.inputValue = this.inputValue.substring(0, max);
        this.name = this.inputValue;
      }
    },

    onBlur() {
      const max = 8;
      const input = document.querySelector('#nameInput');

      input.value = this.inputValue.substring(0, max);
      this.name = input.value;;
      this.inputLength = input.value.length;

      this.validInput =
        this.inputLength > max
          ? false
          : this.inputLength > 0
          ? /^[A-Za-z0-9]+$/.test(this.inputValue)
          : true;
    },

    handleFormSubmit(event) {
      event.preventDefault();
      store.setCompanyName(this.name);
      this.$router.replace({ name: 'usersJoined' });
    },

    setAnimateDanger(bool) {
      this.aimateDanger = bool;
    },
  },
};
</script>

<style lang="scss">
.home_bg {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  background-color: rgba($color-black, $view-overlay-alpha);
  @include helper__box-shadow_overlap($color-black);
}

.home {
  position: relative;
}

.home__name-input {
  grid-row: 2;
}

.home__headline {
  @include font-headline;
  text-align: center;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  width: 320px;
}

.home__scrollhint {
  margin-bottom: calc(20px + env(safe-area-inset-bottom));
}

.home__header {
  font-size: 1.6rem;
  max-width: 330px;
  margin-top: 80px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;

  @include helper__greater($bp-1024) {
    font-size: 2.4rem;
  }
}

.input-company {
  margin: 0 auto;
  max-width: 330px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: opacity 0.3s;

  .headline {
    text-align: center;
    font-size: 1.875rem;
  }

  form {
    display: flex;
    flex-direction: column;

    span {
      color: $color-grey;
      margin-top: 28px;
      @include font-base;
      text-align: center;
      transition: color 0.25s;

      &.color-red {
        color: $color-purple;
      }
    }

    .line {
      position: relative;
      width: 100%;
      height: 1px;

      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: $color-white;
        opacity: 0.3;
      }
    }

    .progress-line {
      width: 100%;
      height: 2px;
      background: $color-white;
      transform-origin: left center;
      will-change: transform;
      box-shadow: 0 0 40px #ffffff;
      transition: all 0.2s linear;

      &.animate-danger {
        background: $color-purple;
        box-shadow: 0 0 10px $color-purple;
      }

      &.color-red {
        background: $color-purple;
        box-shadow: 0 0 10px $color-purple;
      }
    }
  }

  @include helper__until($bp-1024) {
    form {
      margin-top: 40px;

      span {
        margin-top: 24px;
      }
    }
  }

  input {
    color: $color-white;
    text-align: center;
    @include answer-label;

    &[type='text'] {
      padding: 40px 0;
      font-size: 1.78rem;
      text-transform: uppercase;
    }
  }

  @include helper__until($bp-1024) {
    input {
      &[type='text'] {
        padding: 25px 0;
        font-size: 1.5rem;
      }
    }
  }

  input::placeholder {
    color: $color-white;
    opacity: 0.8;
  }
}

.home__continue_button {
  margin-bottom: 80px;

  input {
    color: $color-white;
    cursor: inherit;
  }

  &.mobile-only {
    display: none;
    margin-bottom: 0;
  }

  @include helper__until($bp-1024) {
    &.mobile-only {
      display: block;
      margin-top: 24px;
    }

    &.desktop-only {
      display: none;
    }
  }
}
</style>
